// The four use cases the client scope defines, as ordered step lists.
//
// Transcribed from "WA_Cyber Concierge Platform: Scope Document v3", section
// 2.1 "Step-by-Step Content for Victims of Common Cyber-Fraud Scenarios". Each
// use case has two branches, and the scope writes them as two separate lists:
// (a) when money has been lost, (b) when the attempt did not take money. The
// branches are NOT the same list with a step removed. The money-lost branch of
// SIM swap has seven steps and the attempt branch has four, so they are stored
// as two lists rather than derived from one.
//
// The titles are the scope's own step headings, verbatim. Nothing here is
// invented and nothing is reordered: the order IS the requirement, and a
// journey that delivers the right facts in the wrong order has failed the
// scope, not passed it with a note.

export type JourneyBranch = "money_lost" | "fraud_attempt";

export interface JourneyStep {
  /** 1-based position in the scope's list. The order is the requirement. */
  order: number;
  title: string;
}

export interface JourneyDefinition {
  /** Stable key the harness reports on a replay. */
  use_case: string;
  label: string;
  branch: JourneyBranch;
  steps: JourneyStep[];
}

const steps = (titles: string[]): JourneyStep[] =>
  titles.map((title, i) => ({ order: i + 1, title }));

export const JOURNEYS: JourneyDefinition[] = [
  {
    use_case: "digital_payment_fraud",
    label: "Use case 1 · Digital payment fraud (UPI / QR code scam)",
    branch: "money_lost",
    steps: steps([
      "UPI PIN & account security (do this immediately)",
      "Contact your bank",
      "Call the national cyber-fraud helpline 1930",
      "File a complaint on the National Cybercrime Reporting Portal",
      "Lodge an FIR with the police / cyber cell",
    ]),
  },
  {
    use_case: "digital_payment_fraud",
    label: "Use case 1 · Digital payment fraud (UPI / QR code scam)",
    branch: "fraud_attempt",
    steps: steps([
      "UPI PIN & credentials",
      "Contact your bank & UPI service",
      "File a complaint on the National Cybercrime Reporting Portal",
    ]),
  },
  {
    use_case: "phishing",
    label: "Use case 2 · Phishing (SMS / email / WhatsApp scam)",
    branch: "money_lost",
    steps: steps([
      "Change UPI PIN & all compromised passwords",
      "Notify your bank and UPI provider",
      "Dial 1930 if any funds or sensitive information were compromised",
      "File a cybercrime complaint",
      "Lodge an FIR with the police / cyber cell",
    ]),
  },
  {
    use_case: "phishing",
    label: "Use case 2 · Phishing (SMS / email / WhatsApp scam)",
    branch: "fraud_attempt",
    steps: steps([
      "Reset passwords & enable 2-factor authentication",
      "Notify your bank and service providers",
      "File a complaint on the National Cybercrime Reporting Portal",
    ]),
  },
  {
    use_case: "vishing",
    label: "Use case 3 · Vishing (phone scam / call-merging fraud)",
    branch: "money_lost",
    steps: steps([
      "Change UPI PIN & all compromised passwords",
      "Notify your bank and UPI provider",
      "Call the 1930 helpline",
      "File a cybercrime complaint",
      "Lodge an FIR with the police / cyber cell",
    ]),
  },
  {
    use_case: "vishing",
    label: "Use case 3 · Vishing (phone scam / call-merging fraud)",
    branch: "fraud_attempt",
    steps: steps([
      "Reset passwords & enable 2-factor authentication",
      "Notify your bank and service providers",
      "File a complaint on the National Cybercrime Reporting Portal",
    ]),
  },
  {
    use_case: "sim_swap",
    label: "Use case 4 · SIM swap / cloning",
    branch: "money_lost",
    steps: steps([
      "Secure your UPI PIN & account credentials",
      "Notify your bank and UPI provider",
      "Contact your mobile service provider",
      "Call the 1930 helpline",
      "Check and report via Sanchar Saathi",
      "File a cybercrime complaint",
      "Lodge an FIR with the police / cyber cell",
    ]),
  },
  {
    use_case: "sim_swap",
    label: "Use case 4 · SIM swap / cloning",
    branch: "fraud_attempt",
    steps: steps([
      "Contact your mobile provider immediately",
      "Reset passwords & enable 2-factor authentication",
      "Notify your bank and service providers",
      "File a complaint on the National Cybercrime Reporting Portal",
    ]),
  },
];

export const BRANCH_LABEL: Record<JourneyBranch, string> = {
  money_lost: "money lost",
  fraud_attempt: "fraud attempt, no money lost",
};

export function journeyDefinition(
  useCase: string | null | undefined,
  branch: string | null | undefined
): JourneyDefinition | null {
  const uc = String(useCase ?? "").trim();
  const br = String(branch ?? "").trim();
  return JOURNEYS.find((j) => j.use_case === uc && j.branch === br) ?? null;
}

// ------------------------------------------------------- the replay contract
//
// What the harness has to report per replayed conversation for the Journey tab
// to say anything. It is kept beside the step lists because the two are one
// contract: `step_order` indexes the list above.
//
// EVERY FIELD THAT CAN BE UNOBSERVED IS NULLABLE, and null means nobody
// measured it. It must never be sent as 0 or false. The tab prints "not
// measured" for a null and would print a verdict for a false.

export interface JourneyStepDelivery {
  /** 1-based, indexes the scope list for this use case and branch. */
  step_order: number;
  delivered: boolean;
  /** Which turn carried it. null when it was never delivered. */
  turn_index: number | null;
  /**
   * How many scope steps that ONE outbound message carried. 1 is the
   * requirement. null means nobody counted; it is never 0 for a delivered step.
   */
  steps_in_same_message: number | null;
  /** Did that message ask the user to confirm before moving on? null = unmeasured. */
  confirmation_asked: boolean | null;
  /** Language of the message that carried it, as a BCP-47-ish tag. null = unmeasured. */
  language: string | null;
}

export interface JourneyReplay {
  run_id: string;
  conversation_id: string;
  case_id: string | null;
  /** One of the four use_case keys above. */
  use_case: string;
  branch: JourneyBranch;
  /** The language the user opened in. null = unmeasured, and the language check cannot run. */
  user_language: string | null;
  steps: JourneyStepDelivery[];
  /** Did the conversation reach a close? null = unmeasured. */
  closed: boolean | null;
  close_turn_index: number | null;
  turns: number | null;
}

export interface JourneysResponse {
  run_id: string;
  journeys: JourneyReplay[];
}
